// src/components/DashboardLayout.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SidebarAsistentes from './SidebarAsistentes';
import { getSafeToken } from '../utils/tokenHelper';
import VisualWrapper from './VisualWrapper';
import MessagesNotificationBadge from './MessagesNotificationBadge';

// Helper function para obtener la URL del backend con fallback robusto
const getBackendUrl = () => {
  const envUrl = import.meta.env.VITE_BACKEND_URL;
  // Si la variable de entorno no está definida, usar la URL de producción
  return envUrl && envUrl !== 'undefined' ? envUrl : 'https://edutechpulselab.com/api';
};

const DashboardLayout = ({ children, sidebarVisible: externalSidebarVisible, setSidebarVisible: externalSetSidebarVisible, hideSidebar = false, hideHeader = false }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Estado interno del sidebar como fallback
  const [internalSidebarVisible, setInternalSidebarVisible] = useState(false);
  
  // Usar props externas si están disponibles, sino usar estado interno
  const sidebarVisible = externalSidebarVisible !== undefined ? externalSidebarVisible : internalSidebarVisible;
  const setSidebarVisible = externalSetSidebarVisible || setInternalSidebarVisible;
  
  // Estados del dashboard
  const [allUserChats, setAllUserChats] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [isCreatingNewChat, setIsCreatingNewChat] = useState(false);
  const [loading, setLoading] = useState(false);

  const MAX_CHATS_PER_ASSISTANT = 10;

  // Cargar chats al iniciar
  useEffect(() => {
    if (isAuthenticated) {
      loadUserChats();
    }
  }, [isAuthenticated]);

  const loadUserChats = async () => {
    try {
      setLoading(true);
      
      // TODO: Implementar API de chats cuando sea necesario
      // Por ahora usamos array vacío hasta reescribir la lógica
      console.log('📝 Sistema de chats temporalmente deshabilitado');
      setAllUserChats([]);
      
      // Código comentado temporalmente:
      // const token = localStorage.getItem('token');
      // const response = await fetch(`${getBackendUrl()}/api/chats/user-chats`, {
      //   method: 'GET',
      //   headers: {
      //     'Authorization': `Bearer ${token}`,
      //     'Content-Type': 'application/json'
      //   }
      // });
      
      if (false) { // Deshabilitado temporalmente
        console.error('Error loading user chats:', response.statusText);
        setAllUserChats([]);
      }
    } catch (error) {
      console.error('Error loading chats:', error);
      setAllUserChats([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAssistantForNewChat = useCallback((assistantId) => {
    setSelectedAssistant(assistantId);
    setSelectedChatId(null);
    setIsCreatingNewChat(true);
    
    // Navegar a la página correspondiente
    if (assistantId === 'pmfast') {
      navigate('/pmfast/chat');
    } else {
      navigate(`/chat/${assistantId}`);
    }
  }, [navigate]);

  const handleSelectChat = useCallback((chatId, assistantId) => {
    setSelectedChatId(chatId);
    setSelectedAssistant(assistantId);
    setIsCreatingNewChat(false);
    
    // Navegar al chat específico
    if (assistantId === 'pmfast') {
      navigate(`/pmfast/chat?chatId=${chatId}`);
    } else {
      navigate(`/chat/${assistantId}?chatId=${chatId}`);
    }
  }, [navigate]);

  const handleDeleteChat = useCallback(async (chatId) => {
    try {
      // Eliminar del servidor
      const token = getSafeToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${getBackendUrl()}/chats/${chatId}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        setAllUserChats(prevChats => prevChats.filter(chat => chat.id !== chatId));
        
        if (selectedChatId === chatId) {
          setSelectedChatId(null);
        }
      } else {
        console.error('Error deleting chat:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  }, [selectedChatId]);

  const handleRenameChat = useCallback(async (chatId, newTitle) => {
    try {
      // Actualizar en servidor
      const token = getSafeToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${getBackendUrl()}/chats/${chatId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ title: newTitle })
      });

      if (response.ok) {
        setAllUserChats(prevChats => 
          prevChats.map(chat => 
            chat.id === chatId ? { ...chat, title: newTitle } : chat
          )
        );
      } else {
        console.error('Error renaming chat:', response.statusText);
      }
    } catch (error) {
      console.error('Error renaming chat:', error);
    }
  }, []);

  const handleSelectRolPlay = useCallback(() => {
    // Navegar al dashboard y establecer estado para activar RolPlay
    navigate('/dashboard');
    setSelectedAssistant("rolplay");
  }, [navigate]);

  const handleBackToDashboard = useCallback(() => {
    // Limpiar selección y navegar al dashboard principal
    navigate('/dashboard');
    setSelectedAssistant(null);
  }, [navigate]);

  // Handler para crear simulación de Rol Play
  const handleCreateRolPlayChat = useCallback(async (categoria) => {
    try {
      console.log('Iniciando creación de simulación para categoría:', categoria.key);
      
      // Crear título dinámico basado en la categoría
      const categoryTitles = {
        "cliente_dificil": "Cliente Difícil",
        "negociacion_estrategica": "Negociación Estratégica", 
        "reunion_jefe": "Reunión con Jefe",
        "presentacion_critica": "Presentación Crítica"
      };
      
      const dynamicChatTitle = `Simulación: ${categoryTitles[categoria.key]} ${new Date().toLocaleString()}`;
      
      setIsCreatingNewChat(true);
      
      // Llamada al API para crear el chat
      const token = getSafeToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${getBackendUrl()}/chats`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          asistente: "rolplay",
          titulo: dynamicChatTitle,
          metadata: { categoria: categoria.key }
        })
      });

      if (response.ok) {
        const responseData = await response.json();
        const newChat = responseData.chat;
        
        // Actualizar el estado con el nuevo chat
        setAllUserChats(prevChats => [
          ...prevChats.filter(chat => chat.assistant_id !== 'rolplay'),
          { ...newChat, assistant_id: 'rolplay' }
        ]);
        
        setSelectedAssistant("rolplay");
        setSelectedChatId(newChat._id);
        
        // Navegar al chat específico
        navigate(`/chat/rolplay?chatId=${newChat._id}`);
        
      } else {
        throw new Error('Error al crear el chat');
      }
    } catch (error) {
      console.error("Error al crear simulación de Rol Play:", error);
      alert(`Error al crear la simulación: ${error.message || 'Error desconocido'}`);
    } finally {
      setIsCreatingNewChat(false);
    }
  }, [navigate]);

  const handleOverlayClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (sidebarVisible) {
      setSidebarVisible(false);
    }
  }, [sidebarVisible, setSidebarVisible]);

  const currentUserName = user?.nombre || user?.name || 'Desarrollador';
  const currentUserEmail = user?.email || '';
  
  // 🐛 DEBUG temporal
  console.log('🔍 DashboardLayout - User:', { 
    nombre: user?.nombre, 
    email: user?.email,
    currentUserEmail 
  });

  return (
    <VisualWrapper sidebarVisible={sidebarVisible} setSidebarVisible={setSidebarVisible} hideHeader={hideHeader}>
      {/* Badge de notificaciones de mensajes */}
      <MessagesNotificationBadge />

      {/* Sidebar solo visible como drawer/overlay en mobile/tablet (≤1024px) */}
      {!hideSidebar && (
      <SidebarAsistentes
        userName={currentUserName}
        userEmail={currentUserEmail}
        allUserChats={allUserChats}
        handleSelectAssistantForNewChat={handleSelectAssistantForNewChat}
        handleSelectChat={handleSelectChat}
        handleDeleteChat={handleDeleteChat}
        handleRenameChat={handleRenameChat}
        selectedAssistant={selectedAssistant}
        selectedChatId={selectedChatId}
        expanded={expanded}
        setExpanded={setExpanded}
        sidebarVisible={sidebarVisible}
        setSidebarVisible={setSidebarVisible}
        isCreatingNewChat={isCreatingNewChat}
        MAX_CHATS_PER_ASSISTANT={MAX_CHATS_PER_ASSISTANT}
        loading={loading}
        isAuthenticated={isAuthenticated}
        handleSelectRolPlay={handleSelectRolPlay}
        onBackToDashboard={handleBackToDashboard}
      />
      )}

      {/* Overlay para mobile/tablet (≤1024px) */}
      {sidebarVisible && (
        <div
          className="fixed inset-0 bg-black/40 z-40 max-[1024px]:block min-[1025px]:hidden cursor-pointer"
          onClick={handleOverlayClick}
          onTouchEnd={handleOverlayClick}
          role="button"
          tabIndex={0}
          aria-label="Cerrar sidebar"
          style={{ 
            touchAction: 'none',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)'
          }}
        />
      )}

      {/* Contenido principal: 100% ancho en mobile/tablet, flex-1 en desktop */}
      <main className="flex-1 bg-gray-50 flex flex-col min-h-0 overflow-y-auto w-full">
        {React.cloneElement(children, {
          selectedAssistant,
          selectedChatId,
          setSelectedAssistant,
          allUserChats,
          loading,
          isCreatingNewChat,
          onCreateRolPlayChat: handleCreateRolPlayChat,
          onBackToDashboard: handleBackToDashboard,
          onSelectAssistant: handleSelectAssistantForNewChat,
          onSelectChat: handleSelectChat,
          onDeleteChat: handleDeleteChat,
          onRenameChat: handleRenameChat
        })}
      </main>
    </VisualWrapper>
  );
};

export default DashboardLayout;